import type {
  RoomTypeValue,
  RoomSketchingRequest,
} from '@/entities/visit-counsel/model/house-sketch.types';

export interface FlowRoom {
  id: string;
  label: string;
  tags: string[];
  /**
   * 정식 RoomType. transform step 출력에는 항상 채워진다.
   * 사용자가 terminal 편집 모드에서 "+ 방 추가" 로 새로 만든 row 만 type 이 누락되며,
   * 그 경우 id 패턴 / label 한국어로 fallback 추론한다 (`roomToType`).
   *
   * 이 필드를 가지고 다니는 이유는, transform 이 "1번 방"·"2번 방"처럼 index 가 붙은
   * label 을 만들고 나면 label 만으로는 정식 type 을 더 이상 신뢰성 있게 복원할 수
   * 없기 때문 — 다중 인스턴스 ROOM 이 OTHER_SPACE 로 잘못 매핑되어 namePrefix 가
   * "1번 방" 으로 새는 버그가 있었다.
   */
  type?: RoomTypeValue;
}

/**
 * Firestore transform step이 accumulated.rooms에 출력하는 포맷.
 * { type, index, metadata } — namePrefix 없음, metadata로 방 속성 표현.
 */
export interface TransformRoom {
  type: RoomTypeValue;
  index: number;
  metadata: Record<string, unknown>;
}

const TYPE_TO_LABEL: Record<RoomTypeValue, string> = {
  ENTRANCE: '현관',
  LIVING_ROOM: '거실',
  KITCHEN: '주방',
  ROOM: '방',
  BATH_ROOM: '화장실',
  BALCONY: '베란다',
  RESIDENCE_STORAGE: '세대창고',
  OTHER_SPACE: '기타',
};

/**
 * 정식 RoomType → API 의 namePrefix 로 보낼 한국어 베이스 라벨.
 *
 * - OTHER_SPACE 는 사용자가 입력한 자유 입력 이름(예: "다락방")이 namePrefix 가
 *   되어야 하므로 여기서는 사용하지 않는다 (호출부에서 별도 처리).
 * - 베란다/발코니 는 BALCONY 단일 타입이지만 사용자가 입력한 표기를 가능한 한
 *   유지하기 위해 호출부가 label 한국어를 우선 검사할 수 있다 (현재는 표준 "베란다").
 */
const TYPE_TO_NAME_PREFIX: Record<RoomTypeValue, string> = {
  ENTRANCE: '현관',
  LIVING_ROOM: '거실',
  KITCHEN: '주방',
  ROOM: '방',
  BATH_ROOM: '화장실',
  BALCONY: '베란다',
  RESIDENCE_STORAGE: '세대창고',
  OTHER_SPACE: '기타',
};

/** label 앞에 transform 이 붙인 "N번 " ordinal prefix 가 있으면 떼어낸다. */
function stripOrdinalPrefix(label: string): string {
  return label.replace(/^\d+번\s*/, '').trim() || label.trim();
}

/**
 * Firestore transform 출력(TransformRoom[])을 DoneScreen 표시용 FlowRoom[]으로 변환한다.
 * - metadata.TOILET → tags: ['화장실']
 * - metadata.BALCONY → tags: ['베란다']
 * - OTHER_SPACE.metadata.name → label로 사용
 */
export function transformRoomsToFlowRooms(rooms: TransformRoom[]): FlowRoom[] {
  // 타입별 최대 index를 미리 파악해 2개 이상인 타입에 index 표시
  const typeMaxIndex: Partial<Record<RoomTypeValue, number>> = {};
  for (const room of rooms) {
    typeMaxIndex[room.type] = Math.max(typeMaxIndex[room.type] ?? 0, room.index);
  }

  return rooms.map((room, idx) => {
    const baseLabel = TYPE_TO_LABEL[room.type] ?? room.type;
    let label: string;

    if (room.type === 'OTHER_SPACE') {
      label = typeof room.metadata.name === 'string' ? room.metadata.name : '기타';
    } else if ((typeMaxIndex[room.type] ?? 0) > 1) {
      // 같은 타입이 2개 이상이면 "1번 방", "2번 방" 형태로 표시
      label = `${room.index}번 ${baseLabel}`;
    } else {
      label = baseLabel;
    }

    const tags: string[] = [];
    if (room.metadata.TOILET) tags.push('화장실');
    if (room.metadata.BALCONY) tags.push('베란다');

    return { id: `t_${room.type}_${room.index}_${idx}`, label, tags, type: room.type };
  });
}

// ── ID / 라벨 → 방 타입 매핑 ────────────────────────────────────────────────

const ID_TO_TYPE: Record<string, RoomTypeValue> = {
  entrance: 'ENTRANCE',
  living_room: 'LIVING_ROOM',
  shared_bathroom: 'BATH_ROOM',
  extra_storage: 'RESIDENCE_STORAGE',
  extra_veranda: 'BALCONY',
};

const LABEL_TO_TYPE: Record<string, RoomTypeValue> = {
  현관: 'ENTRANCE',
  거실: 'LIVING_ROOM',
  화장실: 'BATH_ROOM',
  '공용 화장실': 'BATH_ROOM',
  주방: 'KITCHEN',
  발코니: 'BALCONY',
  베란다: 'BALCONY',
  세대창고: 'RESIDENCE_STORAGE',
  방: 'ROOM',
};

function roomToType(room: FlowRoom): RoomTypeValue {
  const { id, label } = room;

  if (/^room_\d+$/.test(id)) return 'ROOM';

  const knownType = ID_TO_TYPE[id];
  if (knownType) return knownType;

  // label 이 transform 의 "1번 방" 같은 prefix 가 붙어있을 수도 있어 strip 후 lookup
  const baseLabel = stripOrdinalPrefix(label);
  if (id.startsWith('extra_') || id.startsWith('add_')) {
    return LABEL_TO_TYPE[baseLabel] ?? 'OTHER_SPACE';
  }

  return LABEL_TO_TYPE[baseLabel] ?? 'OTHER_SPACE';
}

/**
 * house-structure-record-flow의 transform step 출력(accumulated.rooms)을
 * POST /sketching-session/start 에 사용하는 RoomSketchingRequest[] 로 변환한다.
 *
 * rooms 입력: { id, label, tags, type? }[] (transform step 또는 terminal 편집 결과)
 *
 * - FlowRoom.type 이 들어있으면 그것을 신뢰한다 (transform 출력 — 항상 신뢰 가능).
 * - 없으면 id 패턴 / label 한국어로 추론 (사용자가 + 방 추가 로 만든 row).
 *
 * namePrefix 는 항상 "방", "거실", "화장실" 같은 베이스 한국어 라벨로 보낸다.
 * "1번 방" 처럼 transform 이 붙인 ordinal prefix 가 새어나가면 backend 가
 * displayLabel 을 만들 때 ordinal 을 두 번 덧붙여 "1번1번 방" 같은 라벨이
 * 생기는 버그가 있었다 (room-observing picker 에서 발견).
 */
export function mapHouseStructureFlowResult(
  accumulated: Record<string, unknown>
): RoomSketchingRequest[] {
  // terminal 편집 확인 결과(onUpdateAccumulated로 저장) 우선 사용
  const raw = Array.isArray(accumulated._edited_rooms)
    ? (accumulated._edited_rooms as FlowRoom[])
    : Array.isArray(accumulated.rooms)
      ? (accumulated.rooms as FlowRoom[])
      : [];

  const typeCounts: Partial<Record<RoomTypeValue, number>> = {};

  return raw.flatMap((room) => {
    const type = room.type ?? roomToType(room);
    typeCounts[type] = (typeCounts[type] ?? 0) + 1;
    const namePrefix = resolveNamePrefix(room, type);
    return [{ type, namePrefix, index: typeCounts[type]! }];
  });
}

/**
 * FlowRoom + 정식 type → API namePrefix.
 *
 * - OTHER_SPACE: 사용자가 입력한 이름(예: "다락방"). label 에서 ordinal prefix 만 떼어 사용.
 * - 그 외: 항상 정식 베이스 한국어(예: "방", "거실"). label 에 의존하지 않는다.
 *
 * "+ 방 추가" 로 만든 row (type 미지정 → roomToType 가 추론)도 동일 규칙 적용.
 */
function resolveNamePrefix(room: FlowRoom, type: RoomTypeValue): string {
  if (type === 'OTHER_SPACE') {
    // OTHER_SPACE 는 사용자 자유 입력 이름이 namePrefix.
    // transform 출력은 metadata.name 을 label 에 그대로 담고, edit 에서 "그 외" 로
    // 추가한 경우도 사용자 입력값이 label 이므로 label 만 정제하면 된다.
    return stripOrdinalPrefix(room.label);
  }
  // 알려진 type 은 항상 정식 한국어 베이스 라벨. namePrefix 가 "1번 방" 처럼
  // 누출되지 않게 label 을 신뢰하지 않는다.
  return TYPE_TO_NAME_PREFIX[type];
}
